// Asset lineage (wave 13, asset-protocol.md section 9c): every version an asset's creator made, its forks, "what
// changed" between any two versions (a fork against its parent too) and changes a fork offers back. Types, the
// normalisers for the store service's answers (snake_case → the site's camelCase) and the pure helpers the asset page
// and its tests use. No DOM, no fetch.

import type { AssetSummary, OpenLicence, Value } from "./store.ts";

type Raw = Record<string, unknown>;
const str = (v: unknown, d = ""): string => (typeof v === "string" ? v : d);
const num = (v: unknown): number | undefined => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
const arr = <T = unknown>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const obj = (v: unknown): Raw | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Raw) : null);
const secs = (v: unknown): number => {
  if (typeof v === "number") return v > 1e12 ? Math.floor(v / 1000) : v;
  const t = typeof v === "string" ? Date.parse(v) : NaN;
  return Number.isFinite(t) ? Math.floor(t / 1000) : 0;
};

// ---- refs ------------------------------------------------------------------------------------------------------

/** "<creator>/<name>@<version>" */
export interface Ref {
  id: string;
  version: string;
  handle: string;
  name: string;
}
const REF_RE = /^([a-z0-9][a-z0-9-]{0,63})\/([a-z0-9][a-z0-9-]{0,63})@(\d+\.\d+\.\d+(?:-[0-9a-z.-]+)?)$/i;
export function parseRef(ref: string | null | undefined): Ref | null {
  const m = REF_RE.exec(ref ?? "");
  return m ? { id: `${m[1]}/${m[2]}`, version: m[3]!, handle: m[1]!, name: m[2]! } : null;
}
export const refOf = (id: string, version: string): string => `${id}@${version}`;
/** a store creator id ("cr_bob") or a package id ("bob/x") → the handle */
export const handleOf = (s: string): string => (s.startsWith("cr_") ? s.slice(3) : s.split("/")[0] ?? s);

// ---- shapes ----------------------------------------------------------------------------------------------------

export interface ForkSummary extends AssetSummary {
  /** listed versions */
  versions: number;
  /** its own listed forks */
  forks: number;
  /** changes the parent merged from it */
  merged: number;
  score: number;
}

export interface ForkList {
  count: number;
  forks: ForkSummary[];
}

export interface Ancestor {
  ref: string;
  title: string | null;
  handle: string | null;
}

export interface LineageNode {
  id: string;
  title: string | null;
  handle: string;
  parent: string | null;
  version: string | null;
  forks: LineageNode[];
}

export interface Lineage {
  id: string;
  parent: string | null;
  /** the original at the top of the chain, or null for an original */
  root: string | null;
  /** oldest first */
  ancestors: Ancestor[];
  forks: number;
  descendants: number;
  tree: LineageNode[];
}

export interface ParamChange {
  name: string;
  change: "added" | "removed" | "changed";
  fields?: Record<string, [unknown, unknown]>;
  before?: unknown;
  after?: unknown;
}
export interface ManifestChange {
  field: string;
  before: unknown;
  after: unknown;
}
export interface Hunk {
  a: [number, number];
  b: [number, number];
  /** " ctx", "-old", "+new" */
  lines: string[];
}
export interface FileChange {
  path: string;
  change: "added" | "removed" | "changed";
  size?: [number | null, number | null];
  hunks?: Hunk[];
  /** "binary" | "too_large" | "not_readable" */
  skipped?: string;
}
export interface DiffView {
  from: string;
  to: string;
  params: ParamChange[];
  manifest: ManifestChange[];
  files: FileChange[];
  previews: { from: string | null; to: string | null };
}

export type ProposalStatus = "open" | "merged" | "declined" | "withdrawn";
export interface Proposal {
  id: string;
  /** the package it was offered to */
  package: string;
  /** the fork version offered, "<id>@<version>" */
  from: string;
  fromPackage: string;
  /** the fork's creator (who offered it) */
  fromHandle: string;
  title: string;
  message: string | null;
  status: ProposalStatus;
  mergedVersion: string | null;
  mergedRef: string | null;
  note: string | null;
  createdAt: number;
  decidedAt: number | null;
}

export interface ForkResult {
  id: string;
  version: string;
  status: string;
  parent: string;
}

// ---- normalisers -----------------------------------------------------------------------------------------------

export function normOpenLicence(v: unknown): OpenLicence | null {
  const o = obj(v);
  if (!o) return null;
  return { models: str(o.models) || null, code: str(o.code) || null, forkable: o.forkable === true };
}

/** One entry of GET …/forks: a package summary plus its counts. `summary` is store-client's normSummary. */
export function normFork(r: Raw, summary: (r: Raw) => AssetSummary): ForkSummary {
  return { ...summary(r), versions: num(r.versions) ?? 0, forks: num(r.forks) ?? 0, merged: num(r.merged) ?? 0, score: num(r.score) ?? 0 };
}

function normNode(r: unknown): LineageNode | null {
  const o = obj(r);
  const id = str(o?.id);
  if (!o || !id) return null;
  return {
    id,
    title: str(o.title) || null,
    handle: o.creator ? handleOf(str(o.creator)) : handleOf(id),
    parent: str(o.parent) || null,
    version: str(o.version) || null,
    forks: arr(o.forks).map(normNode).filter((n): n is LineageNode => !!n),
  };
}

export function normLineage(r: unknown, id = ""): Lineage {
  const o = obj(r) ?? {};
  return {
    id: str(o.id, id),
    parent: str(o.parent) || null,
    root: str(o.root) || null,
    ancestors: arr(o.ancestors)
      .map((a) => obj(a))
      .filter((a): a is Raw => !!a && !!str(a.ref))
      .map((a) => ({ ref: str(a.ref), title: str(a.title) || null, handle: str(a.creator) ? handleOf(str(a.creator)) : (parseRef(str(a.ref))?.handle ?? null) })),
    forks: num(o.forks) ?? 0,
    descendants: num(o.descendants) ?? num(o.forks) ?? 0,
    tree: arr(o.tree).map(normNode).filter((n): n is LineageNode => !!n),
  };
}

export function normDiff(r: unknown, preview: (u: string | null) => string | null = (u) => u): DiffView {
  const o = obj(r) ?? {};
  const pv = obj(o.previews) ?? {};
  const change = (v: unknown): "added" | "removed" | "changed" => (v === "added" || v === "removed" ? v : "changed");
  return {
    from: str(o.from),
    to: str(o.to),
    params: arr<Raw>(o.params)
      .filter((p) => obj(p) && str(p.name))
      .map((p) => ({ name: str(p.name), change: change(p.change), fields: (obj(p.fields) as ParamChange["fields"]) ?? undefined, before: p.before, after: p.after })),
    manifest: arr<Raw>(o.manifest)
      .filter((m) => obj(m) && str(m.field))
      .map((m) => ({ field: str(m.field), before: m.before ?? null, after: m.after ?? null })),
    files: arr<Raw>(o.files)
      .filter((f) => obj(f) && str(f.path))
      .map((f) => ({
        path: str(f.path),
        change: change(f.change),
        size: Array.isArray(f.size) ? (f.size as [number | null, number | null]) : undefined,
        hunks: Array.isArray(f.hunks) ? arr<Raw>(f.hunks).map((h) => ({ a: h.a as [number, number], b: h.b as [number, number], lines: arr<string>(h.lines).filter((l) => typeof l === "string") })) : undefined,
        skipped: str(f.skipped) || undefined,
      })),
    previews: { from: preview(str(pv.from) || null), to: preview(str(pv.to) || null) },
  };
}

const PROPOSAL_STATUS = new Set(["open", "merged", "declined", "withdrawn"]);
export function normProposal(r: unknown): Proposal | null {
  const o = obj(r);
  const id = str(o?.id);
  if (!o || !id) return null;
  const from = str(o.from);
  const fromPackage = str(o.from_package) || (parseRef(from)?.id ?? "");
  const status = str(o.status);
  return {
    id,
    package: str(o.package),
    from,
    fromPackage,
    fromHandle: handleOf(fromPackage || from),
    title: str(o.title),
    message: str(o.message) || null,
    status: (PROPOSAL_STATUS.has(status) ? status : "open") as ProposalStatus,
    mergedVersion: str(o.merged_version) || null,
    mergedRef: str(o.merged_ref) || null,
    note: str(o.note) || null,
    createdAt: secs(o.created_at),
    decidedAt: secs(o.decided_at) || null,
  };
}

export function normForkResult(r: unknown, fallbackId = ""): ForkResult {
  const o = obj(r) ?? {};
  return { id: str(o.id, fallbackId), version: str(o.version), status: str(o.status, "uploading"), parent: str(o.forked_from) || str(o.parent) };
}

// ---- licence -----------------------------------------------------------------------------------------------------

const SPDX: Record<string, string> = { "CC-BY-4.0": "CC BY 4.0", "CC-BY-SA-4.0": "CC BY-SA 4.0", MIT: "MIT", "Apache-2.0": "Apache 2.0" };
export const spdxName = (id: string): string => SPDX[id] ?? id;

/**
 * The open licence in one line: "CC BY 4.0 (models) · MIT (code) · forkable with credit", or "Not open for forks".
 * Unknown (an older store): free assets are forkable with the defaults (owner decision 3), paid ones closed.
 */
export function openLicenceLabel(lic: OpenLicence | null | undefined, price = 0, hasCode = true): string {
  const l = lic ?? (price > 0 ? { models: null, code: null, forkable: false } : { models: "CC-BY-4.0", code: hasCode ? "MIT" : null, forkable: true });
  if (!l.forkable) return "Not open for forks";
  const parts = [l.models ? `${spdxName(l.models)} (models)` : null, l.code ? `${spdxName(l.code)} (code)` : null].filter(Boolean);
  return [...parts, price > 0 ? "forkable with credit, by people who own a copy" : "forkable with credit"].join(" · ");
}

export const isForkable = (lic: OpenLicence | null | undefined, price = 0): boolean => (lic ? lic.forkable : price === 0);

// ---- versions ----------------------------------------------------------------------------------------------------

/** The version a merge suggests: the next minor after the latest ("1.2.3" → "1.3.0"). */
export function nextMinor(latest: string | null | undefined): string {
  const m = /^(\d+)\.(\d+)\.(\d+)/.exec(latest ?? "");
  return m ? `${m[1]}.${Number(m[2]) + 1}.0` : "1.0.0";
}

export const isSemver = (v: string): boolean => /^\d+\.\d+\.\d+(?:-[0-9a-z.-]+)?$/i.test(v.trim());

// ---- what changed, in words -------------------------------------------------------------------------------------

const FIELD_LABEL: Record<string, string> = {
  default: "Starts at",
  minimum: "Lowest",
  maximum: "Highest",
  enum: "Choices",
  type: "Type",
  format: "Format",
  description: "Description",
  title: "Name",
};
export const fieldLabel = (k: string): string => FIELD_LABEL[k] ?? k.charAt(0).toUpperCase() + k.slice(1);

/** A schema or manifest value for people: lists joined, booleans as on/off, nothing as "—". */
export function fmtValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "on" : "off";
  if (typeof v === "number") return String(Number(v.toFixed(6)));
  if (typeof v === "string") return v;
  if (Array.isArray(v)) return v.length ? v.map(fmtValue).join(", ") : "none";
  return JSON.stringify(v);
}

/** A param's schema in a few words: "starts at 1.2, 0 to 3" / "choices: a, b" / "on or off, starts on". */
export function schemaSummary(s: unknown): string {
  const o = obj(s);
  if (!o) return fmtValue(s);
  const bits: string[] = [];
  if (Array.isArray(o.enum)) bits.push(`choices: ${fmtValue(o.enum)}`);
  if (o.type === "boolean") bits.push("on or off");
  if (o.default !== undefined) bits.push(`starts at ${fmtValue(o.default)}`);
  if (typeof o.minimum === "number" && typeof o.maximum === "number") bits.push(`${fmtValue(o.minimum)} to ${fmtValue(o.maximum)}`);
  return bits.join(", ") || fmtValue(o.type ?? o);
}

export interface ParamRow {
  name: string;
  change: ParamChange["change"];
  /** one per changed schema field; added/removed params get one summary row */
  cells: { field: string; before: string; after: string }[];
}

/** Param changes as table rows: before → after per schema field; added and removed params in a few words. */
export function paramRows(changes: ParamChange[], label: (key: string) => string = (k) => k): ParamRow[] {
  return changes.map((c) => {
    if (c.change === "added") return { name: label(c.name), change: c.change, cells: [{ field: "New", before: "—", after: schemaSummary(c.after) }] };
    if (c.change === "removed") return { name: label(c.name), change: c.change, cells: [{ field: "Removed", before: schemaSummary(c.before), after: "—" }] };
    const fields = Object.entries(c.fields ?? {});
    return { name: label(c.name), change: c.change, cells: fields.map(([k, [b, a]]) => ({ field: fieldLabel(k), before: fmtValue(b), after: fmtValue(a) })) };
  });
}

const MANIFEST_LABEL: Record<string, string> = {
  title: "Name",
  kind: "Kind",
  tags: "Tags",
  price: "Price",
  licence: "Licence",
  budget: "Budget",
  conventions: "Rig",
  capabilities: "Code may use",
  physics: "Physics",
  bake: "Ready-made model",
  "params.presets": "Presets",
  "params.required": "Required settings",
  deterministic: "Deterministic",
};
export const manifestLabel = (f: string): string => MANIFEST_LABEL[f] ?? f;

/**
 * A file's name for people: code and text keep their path; models, pictures and sounds say what they are (the full
 * path stays in the element's title).
 */
export function fileLabel(path: string): string {
  const base = path.split("/").pop() ?? path;
  const stem = base.replace(/\.[^.]+$/, "");
  if (/\.(glb|gltf)$/i.test(path)) return stem === "default" ? "Ready-made model" : `Ready-made model: ${stem}`;
  if (path === "preview.png") return "Store picture";
  if (/\.(png|jpe?g|webp|ktx2|basis)$/i.test(path)) return `Texture: ${stem}`;
  if (/\.(ogg|mp3|wav|m4a|opus)$/i.test(path)) return `Sound: ${stem}`;
  return path;
}

/** The skipped-file reasons in words. */
export function skippedText(reason: string | undefined): string | null {
  if (!reason) return null;
  if (reason === "binary") return "Binary file: not shown line by line.";
  if (reason === "too_large") return "Too large to show line by line.";
  if (reason === "not_readable") return "The source needs a licence to read.";
  return reason;
}

export interface HunkLine {
  sign: " " | "-" | "+";
  text: string;
  /** line numbers in the old and new file (null on the side the line isn't in) */
  a: number | null;
  b: number | null;
}

/** A hunk's lines with their line numbers. */
export function hunkLines(h: Hunk): HunkLine[] {
  let a = h.a[0];
  let b = h.b[0];
  return h.lines.map((l) => {
    const sign = (l[0] === "-" || l[0] === "+" ? l[0] : " ") as HunkLine["sign"];
    const out: HunkLine = { sign, text: l.slice(1), a: sign === "+" ? null : a, b: sign === "-" ? null : b };
    if (sign !== "+") a++;
    if (sign !== "-") b++;
    return out;
  });
}

/** "+12 −3" for a file's hunks. */
export function lineCounts(hunks: Hunk[] | undefined): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const h of hunks ?? []) for (const l of h.lines) l[0] === "+" ? added++ : l[0] === "-" ? removed++ : 0;
  return { added, removed };
}

/** Nothing changed that people would see. */
export const diffEmpty = (d: DiffView): boolean => !d.params.length && !d.manifest.length && !d.files.filter((f) => f.path !== "asset.json").length;

/** A one-line summary of a diff: "2 settings changed, 1 new · 1 file". */
export function diffSummary(d: DiffView): string {
  const added = d.params.filter((p) => p.change === "added").length;
  const removed = d.params.filter((p) => p.change === "removed").length;
  const changed = d.params.length - added - removed;
  const files = d.files.filter((f) => f.path !== "asset.json").length;
  const bits = [
    changed ? `${changed} ${changed === 1 ? "setting" : "settings"} changed` : null,
    added ? `${added} new` : null,
    removed ? `${removed} removed` : null,
    d.manifest.length ? `${d.manifest.length} ${d.manifest.length === 1 ? "detail" : "details"}` : null,
    files ? `${files} ${files === 1 ? "file" : "files"}` : null,
  ].filter(Boolean);
  return bits.length ? bits.join(" · ") : "Nothing you'd notice changed";
}

// ---- the store's own diff, for the sample store (backend/store/src/diff.ts, kept small) ---------------------------

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
type M = { params?: { properties?: Record<string, unknown>; order?: string[] }; [k: string]: unknown };

export function paramDiff(a: M, b: M): ParamChange[] {
  const pa = obj(a?.params?.properties) ?? {};
  const pb = obj(b?.params?.properties) ?? {};
  // the newer order first; params it doesn't name (and removed ones) after, so an added param is never missed
  const order = [...(Array.isArray(b?.params?.order) ? b.params!.order! : []), ...Object.keys(pb), ...Object.keys(pa)];
  const out: ParamChange[] = [];
  for (const name of new Set(order)) {
    const x = pa[name];
    const y = pb[name];
    if (x === undefined && y !== undefined) out.push({ name, change: "added", after: y });
    else if (x !== undefined && y === undefined) out.push({ name, change: "removed", before: x });
    else if (x !== undefined && !same(x, y)) {
      const fields: Record<string, [unknown, unknown]> = {};
      const xo = obj(x) ?? {};
      const yo = obj(y) ?? {};
      for (const k of new Set([...Object.keys(xo), ...Object.keys(yo)])) if (!same(xo[k], yo[k])) fields[k] = [xo[k] ?? null, yo[k] ?? null];
      out.push({ name, change: "changed", fields });
    }
  }
  return out;
}

const WATCHED = ["title", "kind", "tags", "price", "licence", "budget", "conventions", "capabilities", "physics", "bake", "params.presets", "params.required", "deterministic"];
export function manifestChanges(a: M, b: M): ManifestChange[] {
  const get = (m: unknown, path: string) => path.split(".").reduce<unknown>((o, k) => obj(o)?.[k], m);
  return WATCHED.flatMap((f) => {
    const x = get(a, f) ?? null;
    const y = get(b, f) ?? null;
    return same(x, y) ? [] : [{ field: f, before: x, after: y }];
  });
}

// ---- using it ----------------------------------------------------------------------------------------------------

/** The CLI line that adds this exact version to a project. */
export const addCommand = (id: string, version: string): string => `thrdm add ${id}@${version}`;
/** The CLI line that forks it under the viewer's handle (a placeholder when they have none). */
export const forkCommand = (id: string, handle: string | null): string => `thrdm fork ${id} --as ${handle || "<you>"}/${id.split("/")[1] ?? id}`;

/**
 * A ready prompt for an AI coding agent with the store's MCP server (`thrdm mcp`): install this version with the
 * settings the viewer picked, then load it with the store's runtime.
 */
export function agentPrompt(id: string, version: string, params: Record<string, Value>, title?: string): string {
  const p = Object.keys(params).length ? ` with params ${JSON.stringify(params)}` : "";
  return [
    `Use the Banger store MCP tool install to add ${id}@${version}${p} to this project${title ? ` (${title})` : ""}.`,
    `If the store's tools aren't connected yet, add its MCP server first: it runs with thrdm mcp.`,
    `Then load it from assets/${id}/ with the store's three.js runtime (loadAsset) and add it to the scene.`,
  ].join(" ");
}

/** The package files "Add to project" writes: every file in the manifest plus asset.json, never outside the folder. */
export function packageFiles(files: Record<string, string> | undefined): string[] {
  const safe = (p: string) => !!p && !p.startsWith("/") && !p.split("/").some((s) => s === ".." || s === "." || s === "");
  return [...new Set([...Object.keys(files ?? {}), "asset.json"])].filter(safe).sort();
}

/** Fork and merge refusals in plain words (status, code from the store). */
export function lineageErrorText(status: number, code: string, fallback = "That didn't work. Try again in a moment."): string {
  const m: Record<string, string> = {
    not_forkable: "Its creator hasn't opened this one for forks.",
    licence_required: "Forking this one needs a copy you own.",
    package_exists: "You already have an asset by that name. Pick another name.",
    not_creator: "That needs your own creator handle. Claim one on your creator dashboard first.",
    creator_age: "Creator features need an 18+ account. Yours moves up to 18+ by itself.",
    already_proposed: "You've already offered this version. It's waiting for the creator.",
    not_a_fork: "Only a fork can offer a change back to its original.",
    bad_version: "Pick a version number newer than the latest, like 1.3.0.",
    version_exists: "That version number is taken. Pick the next one.",
    merge_rejected: "The merged copy didn't pass the automatic checks, so the offer stays open. Fix it and merge under a new number.",
    not_open: "That offer has already been answered.",
    fork_underpriced: "A fork can't cost less than its original.",
    unauthorized: "Sign in first.",
  };
  if (m[code]) return m[code]!;
  if (status === 401) return "Sign in first.";
  if (status === 403) return "Your account can't do that yet.";
  return fallback;
}
